import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';

/**
 * Forgot-password starts here. We submit the email, server always returns 200
 * regardless of whether the address is registered — so we just show a
 * confirmation message instead of a "we sent it" / "no such user" split.
 * Keeps the endpoint from doubling as an account-existence probe.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await api.post('/api/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (ex: unknown) {
      const msg = (ex as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Something went wrong';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="xs">
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Reset password</Typography>
            {submitted
              ? (
                <>
                  <Alert severity="success" sx={{ mt: 1 }}>
                    If an account exists for that email, a reset link is on its way. The link
                    expires in 1 hour. Check your spam folder if you don't see it.
                  </Alert>
                  <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 2 }}>
                    <Link component={RouterLink} to="/login">Back to sign in</Link>
                  </Typography>
                </>
              )
              : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Enter the email on your account — we'll send you a link to set a new password.
                  </Typography>
                  <Stack component="form" onSubmit={onSubmit} spacing={2}>
                    <TextField
                      label="Email" type="email" value={email}
                      onChange={(e) => setEmail(e.target.value)} required size="small"
                      autoComplete="email" autoFocus
                    />
                    {err && <Alert severity="error" variant="outlined">{err}</Alert>}
                    <Button type="submit" variant="contained" disabled={busy} size="large"
                            startIcon={busy ? <CircularProgress size={18} /> : null}>
                      {busy ? 'Sending…' : 'Send reset link'}
                    </Button>
                    <Typography variant="body2" align="center" color="text.secondary">
                      <Link component={RouterLink} to="/login">Back to sign in</Link>
                    </Typography>
                  </Stack>
                </>
              )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
